import { useMemo } from 'react';
import { mask, omit } from 'superstruct';

import {
	fetchGMBTemplate,
	gmbTemplateKeys,
	useListGMBTemplatesQuery,
} from '@adflow/frontend/feat-google-my-business';
import {
	type DataTableFullProps,
	useColumnTemplate,
	useTableDataWithTempRows,
} from '@adflow/frontend/ui-data-table';
import {
	useTableParams,
	useUrlQueryTableFilter,
} from '@adflow/frontend/util-router';
import {
	DEFAULT_GMB_TEMPLATE_SORT,
	DEFAULT_GMB_TEMPLATE_SORT_DIR,
	ListGMBTemplateQueryDto,
} from '@adflow/shared/data-google-my-business';
import type { ViewGMBTemplateDtoWithUploadResult } from '@adflow/shared/util-history';

import { useGMBTemplateColumns } from './useGMBTemplateColumns';

const itemType = 'google-my-business-template';
const minSticky = 1;

export function useGMBTemplatesList({ onDelete }: { onDelete?: () => void }) {
	const tableParams = useTableParams({
		itemType,
		defaultSort: {
			sort: DEFAULT_GMB_TEMPLATE_SORT,
			sortDirection: DEFAULT_GMB_TEMPLATE_SORT_DIR,
		},
	});

	const {
		currentPage,
		rowsPerPage,
		openRows,
		setRowsPerPage,
		setCurrentPage,
		tableWrapRef,
		useQueryTotalEffect,
		sort,
		setSort,
		totalRows,
		searchQuery,
	} = tableParams;

	const { value: filterValues, setValue: setFilterValues } =
		useUrlQueryTableFilter(
			'filter',
			omit(ListGMBTemplateQueryDto, ['page', 'itemsPerPage'])
		);

	const args = mask(
		{
			page: currentPage ?? 0,
			itemsPerPage: rowsPerPage ?? 0,
			...sort,
			...(filterValues ?? { archived: false }),
			search: searchQuery ?? filterValues?.search,
		},
		ListGMBTemplateQueryDto
	);

	const gmbTemplatesQuery = useListGMBTemplatesQuery(
		args,
		Boolean(rowsPerPage)
	);

	useQueryTotalEffect(gmbTemplatesQuery.data?.meta.totalItems);

	const data = useMemo(
		() => gmbTemplatesQuery.data?.items ?? [],
		[gmbTemplatesQuery.data?.items]
	);

	const columnProps = useColumnTemplate({
		columns: useGMBTemplateColumns({ onDelete }),
		itemType,
		minSticky,
	});

	const { tableData } = useTableDataWithTempRows(
		data,
		rowsPerPage ?? data.length,
		openRows,
		(id) => ({
			queryKey: gmbTemplateKeys.view(id),
			queryFn: () => fetchGMBTemplate(id),
		})
	);

	return {
		dataTableProps: {
			...columnProps,
			minSticky,

			tableWrapRef,
			itemType,
			data: tableData,

			usePagination: true,
			currentPage: currentPage ?? 0,
			rowsPerPage: rowsPerPage ?? 0,
			totalItems: gmbTemplatesQuery.data?.meta.totalItems ?? totalRows ?? 0,
			onChangePage: setCurrentPage,
			onChangeRowsPerPage: setRowsPerPage,

			sort,
			setSort,
			isLoading: gmbTemplatesQuery.isLoading,
		} satisfies Partial<DataTableFullProps<ViewGMBTemplateDtoWithUploadResult>>,
		tableParams,
		filterValues,
		setFilterValues,
	};
}
